/* Cardshop theme — vanilla JS, no dependencies. Everything works without it:
   forms post normally, menus fall back to links, filters have a submit button. */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const routes = (window.theme && window.theme.routes) || {};
  const strings = (window.theme && window.theme.strings) || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Runs fn once the next frame has painted, when layout is clean, so reading sizes forces no reflow
  const afterPaint = (fn) => requestAnimationFrame(() => setTimeout(fn, 0));

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(message, isError = false) {
    const el = $('#Toast');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('toast--error', isError);
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (el.hidden = true), 4000);
  }

  /* ---------- Drawers with focus trap ---------- */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let openDrawer = null;
  let lastTrigger = null;

  function open(drawer, trigger) {
    if (!drawer) return;
    if (openDrawer && openDrawer !== drawer) close(openDrawer, false);
    lastTrigger = trigger || document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    openDrawer = drawer;
    $$('[aria-controls="' + drawer.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const target = $('[data-autofocus]', drawer) || $(FOCUSABLE, drawer);
    setTimeout(() => target && target.focus(), reducedMotion ? 0 : 60);
  }

  function close(drawer, restoreFocus = true) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    $$('[aria-controls="' + drawer.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (openDrawer === drawer) openDrawer = null;
    if (restoreFocus && lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.open);
      if (drawer) {
        e.preventDefault();
        open(drawer, opener);
      }
      return;
    }
    const closer = e.target.closest('[data-close]');
    if (closer) {
      e.preventDefault();
      close(closer.closest('.drawer, .search-modal'));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!openDrawer) return;
    if (e.key === 'Escape') {
      close(openDrawer);
      return;
    }
    if (e.key !== 'Tab') return;
    const items = $$(FOCUSABLE, openDrawer).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ---------- Announcement rotator ---------- */
  $$('[data-announcement]').forEach((bar) => {
    const items = $$('.announcement__item', bar);
    if (items.length < 2) return;
    let index = 0;
    let timer;
    const show = (i) => {
      items[index].classList.remove('is-active');
      index = (i + items.length) % items.length;
      items[index].classList.add('is-active');
    };
    const start = () => {
      if (reducedMotion) return;
      stop();
      timer = setInterval(() => show(index + 1), (Number(bar.dataset.speed) || 5) * 1000);
    };
    const stop = () => clearInterval(timer);
    $('[data-prev]', bar)?.addEventListener('click', () => {
      show(index - 1);
      start();
    });
    $('[data-next]', bar)?.addEventListener('click', () => {
      show(index + 1);
      start();
    });
    bar.addEventListener('mouseenter', stop);
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', stop);
    bar.addEventListener('focusout', start);
    start();
  });

  /* ---------- Header: shadow on scroll, dropdowns ---------- */
  const headerWrap = $('.header-wrapper');
  if (headerWrap) {
    const onScroll = () => headerWrap.classList.toggle('is-scrolled', window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    afterPaint(onScroll);
  }

  $$('.nav__item[data-dropdown]').forEach((item) => {
    const button = $('.nav__link', item);
    let hoverTimer;
    const setOpen = (state) => {
      item.classList.toggle('is-open', state);
      button.setAttribute('aria-expanded', String(state));
    };
    button.addEventListener('click', () => setOpen(!item.classList.contains('is-open')));
    item.addEventListener('mouseenter', () => {
      clearTimeout(hoverTimer);
      setOpen(true);
    });
    item.addEventListener('mouseleave', () => {
      hoverTimer = setTimeout(() => setOpen(false), 120);
    });
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) setOpen(false);
    });
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.focus();
      }
    });
  });

  /* ---------- Predictive search ---------- */
  const searchModal = $('#SearchModal');
  if (searchModal) {
    const input = $('input[type="search"]', searchModal);
    const results = $('[data-predictive]', searchModal);
    let controller;
    let debounce;
    const render = async (q) => {
      if (!q.trim()) {
        results.innerHTML = '';
        return;
      }
      controller?.abort();
      controller = new AbortController();
      try {
        const url =
          routes.predictiveSearch +
          '?q=' +
          encodeURIComponent(q) +
          '&resources[type]=product,collection,query&resources[limit]=6&section_id=predictive-search';
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-predictive-content]');
        results.innerHTML = content ? content.innerHTML : '';
      } catch (err) {
        if (err.name !== 'AbortError') results.innerHTML = '';
      }
    };
    input?.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => render(input.value), 220);
    });
    searchModal.addEventListener('click', (e) => {
      if (e.target.classList.contains('search-modal__overlay')) close(searchModal);
    });
  }

  /* ---------- Cart ---------- */
  const cartDrawer = $('#CartDrawer');

  function sectionIds() {
    const ids = [];
    if (cartDrawer) ids.push('cart-drawer');
    const page = $('[data-cart-page]');
    if (page) ids.push(page.dataset.sectionId);
    return ids;
  }

  function applySections(sections) {
    if (!sections) return;
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    if (sections['cart-drawer'] && cartDrawer) {
      const doc = parse(sections['cart-drawer']);
      const next = doc.querySelector('[data-cart-drawer-content]');
      const current = $('[data-cart-drawer-content]', cartDrawer);
      if (next && current) current.replaceWith(next);
      const count = doc.querySelector('[data-cart-drawer-content]')?.dataset.count;
      if (count !== undefined) updateCount(count);
    }
    const page = $('[data-cart-page]');
    if (page && sections[page.dataset.sectionId]) {
      const doc = parse(sections[page.dataset.sectionId]);
      const next = doc.querySelector('[data-cart-page]');
      if (next) {
        page.replaceWith(next);
        updateCount(next.dataset.count);
      }
    }
  }

  function updateCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = Number(count) > 0 ? count : '';
    });
    $$('[data-cart-link]').forEach((el) => {
      el.setAttribute('aria-label', 'Varukorg, ' + count + (Number(count) === 1 ? ' vara' : ' varor'));
    });
  }

  async function addToCart(form, button) {
    const body = new FormData(form);
    const ids = sectionIds();
    if (ids.length) {
      body.append('sections', ids.join(','));
      body.append('sections_url', window.location.pathname);
    }
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    const errorEl = $('[data-buy-error]', form.closest('[data-product]') || form);
    if (errorEl) errorEl.textContent = '';
    try {
      const res = await fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        const msg = data.description || data.message || strings.cartError;
        if (errorEl) errorEl.textContent = msg;
        else toast(msg, true);
        return;
      }
      applySections(data.sections);
      if (cartDrawer && window.theme.cartDrawer) {
        open(cartDrawer, button);
      } else if (!cartDrawer) {
        toast(strings.added);
        refreshCount();
      } else {
        toast(strings.added);
      }
    } catch (e) {
      toast(strings.cartError, true);
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
    }
  }

  async function refreshCount() {
    try {
      const res = await fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      updateCount(cart.item_count);
    } catch (e) {
      /* count stays as it was */
    }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    addToCart(form, e.submitter || $('[type="submit"]', form));
  });

  async function changeLine(key, quantity, row) {
    row?.classList.add('is-loading');
    try {
      const res = await fetch(routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: sectionIds(), sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        toast(data.description || data.message || strings.cartError, true);
        row?.classList.remove('is-loading');
        return;
      }
      const focusKey = document.activeElement?.closest('[data-line-key]')?.dataset.lineKey;
      applySections(data.sections);
      if (focusKey) {
        const again = $('[data-line-key="' + CSS.escape(focusKey) + '"] input');
        again?.focus();
      }
    } catch (e) {
      toast(strings.cartError, true);
      row?.classList.remove('is-loading');
    }
  }

  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-line-step]');
    const remove = e.target.closest('[data-line-remove]');
    const row = e.target.closest('[data-line-key]');
    if (!row || (!step && !remove)) return;
    e.preventDefault();
    const input = $('input', row);
    if (remove) return changeLine(row.dataset.lineKey, 0, row);
    const next = Math.max(0, Number(input.value) + Number(step.dataset.lineStep));
    changeLine(row.dataset.lineKey, next, row);
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-key] input[type="number"]');
    if (!input) return;
    const row = input.closest('[data-line-key]');
    changeLine(row.dataset.lineKey, Math.max(0, Number(input.value) || 0), row);
  });

  /* ---------- Quantity steppers on the product page ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const input = $('input', btn.closest('.qty'));
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (Number(input.value) || min) + Number(btn.dataset.qtyStep)));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Product page ---------- */
  $$('[data-product]').forEach((root) => {
    const variantsEl = $('[data-variants]', root);
    const variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    const idInput = $('[data-variant-id]', root);
    const buyButton = $('[data-buy-button]', root);
    const stickyButton = $('[data-sticky-button]');
    const picker = $('[data-variant-picker]', root);

    if (picker && variants.length) {
      picker.addEventListener('change', () => {
        const chosen = $$('fieldset', picker).map((fs) => $('input:checked', fs)?.value);
        const variant = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
        updateVariant(variant);
      });
    }

    function updateVariant(variant) {
      if (!variant) {
        buyButton.disabled = true;
        $('span', buyButton).textContent = strings.unavailable;
        return;
      }
      idInput.value = variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      const sectionId = root.dataset.sectionId;
      fetch(window.location.pathname + '?variant=' + variant.id + '&section_id=' + sectionId)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          ['[data-price-block]', '[data-stock-block]', '[data-buy-label]', '[data-sticky-price]'].forEach((sel) => {
            const next = doc.querySelector(sel);
            const current = document.querySelector(sel);
            if (next && current) current.innerHTML = next.innerHTML;
          });
          const nextBtn = doc.querySelector('[data-buy-button]');
          if (nextBtn) buyButton.disabled = nextBtn.disabled;
          if (stickyButton && nextBtn) stickyButton.disabled = nextBtn.disabled;
          const qty = doc.querySelector('[data-qty-input]');
          const qtyNow = $('[data-qty-input]', root);
          if (qty && qtyNow) qtyNow.max = qty.max;
        });
      if (variant.featured_media) {
        const slide = $('[data-media-id="' + variant.featured_media.id + '"]', root);
        slide && scrollToSlide(slide);
      }
    }

    /* Gallery */
    const track = $('.gallery__main', root);
    const thumbs = $$('.gallery__thumb', root);
    const dots = $$('.gallery__dots span', root);
    function scrollToSlide(slide) {
      track.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
    }
    thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => {
        const slide = $('[data-media-id="' + thumb.dataset.target + '"]', root);
        if (slide) scrollToSlide(slide);
      })
    );
    if (track && (thumbs.length || dots.length)) {
      const slides = $$('.gallery__slide', track);
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const i = slides.indexOf(entry.target);
            thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
            dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
          });
        },
        { root: track, threshold: 0.6 }
      );
      slides.forEach((s) => io.observe(s));
    }

    /* Sticky buy bar: shows once the main button has scrolled out of view */
    const sticky = $('[data-sticky-buy]');
    if (sticky && buyButton) {
      const io = new IntersectionObserver(
        ([entry]) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sticky.classList.toggle('is-visible', past);
          sticky.setAttribute('aria-hidden', String(!past));
          $$('button, a', sticky).forEach((el) => (el.tabIndex = past ? 0 : -1));
        },
        { threshold: 0 }
      );
      io.observe(buyButton);
      stickyButton?.addEventListener('click', () => {
        const form = buyButton.closest('form');
        if (form.requestSubmit) form.requestSubmit(buyButton);
        else buyButton.click();
      });
    }
  });

  /* ---------- Back link: go back in history when the visitor came from this shop ---------- */
  document.addEventListener('click', (e) => {
    const back = e.target.closest('[data-back]');
    if (!back) return;
    let sameSite = false;
    try {
      sameSite = document.referrer && new URL(document.referrer).origin === window.location.origin;
    } catch (err) {
      sameSite = false;
    }
    if (sameSite && window.history.length > 1) {
      e.preventDefault();
      window.history.back();
    }
  });

  /* ---------- #bevaka: sold-out cards link to the restock box, which stays hidden until STOQ
     has inserted its button, so the browser's own jump to the anchor finds nothing. Scroll once it shows. ---------- */
  if (window.location.hash === '#bevaka') {
    const box = document.getElementById('bevaka');
    if (box) {
      const reveal = () => {
        if (!box.offsetParent) return false;
        box.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
        return true;
      };
      if (!reveal()) {
        const mo = new MutationObserver(() => reveal() && mo.disconnect());
        mo.observe(box, { childList: true, subtree: true });
        setTimeout(() => mo.disconnect(), 8000);
      }
    }
  }

  /* ---------- Dialogs (Klarna "Läs mer") ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialog);
      if (dialog?.showModal) {
        e.preventDefault();
        dialog.showModal();
      }
      return;
    }
    if (e.target.matches('dialog.modal')) e.target.close();
    const closer = e.target.closest('[data-dialog-close]');
    if (closer) closer.closest('dialog')?.close();
  });

  /* ---------- Sliders ---------- */
  function initSlider(slider) {
    const track = $('.slider__track', slider);
    const prev = $('[data-slider-prev]', slider);
    const next = $('[data-slider-next]', slider);
    if (!track || !prev || !next) return;
    const step = () => {
      const item = track.firstElementChild;
      return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : 300;
    };
    const update = () => {
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reducedMotion ? 'auto' : 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reducedMotion ? 'auto' : 'smooth' }));
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    afterPaint(update);
  }
  $$('[data-slider]').forEach(initSlider);

  /* ---------- Product recommendations ---------- */
  $$('[data-recommendations]').forEach(async (el) => {
    try {
      const res = await fetch(el.dataset.url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-recommendations]');
      if (!next || !next.innerHTML.trim()) return el.remove();
      el.innerHTML = next.innerHTML;
      $$('[data-slider]', el).forEach(initSlider);
    } catch (e) {
      el.remove();
    }
  });

  /* ---------- Collection filters and sort: submit on change ---------- */
  document.addEventListener('change', (e) => {
    const form = e.target.closest('[data-filter-form]');
    if (!form) return;
    if (e.target.type === 'number') return;
    form.requestSubmit ? form.requestSubmit() : form.submit();
  });
  $$('[data-filter-form] [data-js-hide]').forEach((el) => (el.hidden = true));

  /* ---------- Scroll reveal ---------- */
  if (!reducedMotion && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        }),
      { rootMargin: '0px 0px -10% 0px' }
    );
    $$('.reveal').forEach((el) => io.observe(el));
  }
})();
